import { useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import {
  Col,
  Container,
  Row,
  OverlayTrigger,
  Tooltip,
  Badge,
  Card,
  Button,
  Form,
} from "react-bootstrap";
import { DataGrid } from "./Controls/DataGrid";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { toast } from "react-toastify";
import { createApiService } from "./shared/apiConfig";
import {
  DataEntitiesWaitlistStatus,
  ServerRequestsGetFacilityWaitlistToggleResponse,
  ServerRequestsWaitlistData,
  WaitlistApi,
} from "./shared/api/axios-client";
import { useLoaderData } from "react-router-dom";
import InviteParkerModal from "./InviteParkerWaitlistModal";
import { useAtom } from "jotai";
import { useParamIds } from "./Data/Common";
import { CheckCircleFill, Trash } from "react-bootstrap-icons";

export default function WaitlistManagementPage() {
  const { facilityId } = useParamIds();
  const loaderData = useLoaderData() as any[];
  const loadedWaitlist = loaderData[1] as ServerRequestsWaitlistData[];
  const loadedWaitlistToggle =
    loaderData[2] as ServerRequestsGetFacilityWaitlistToggleResponse;

  const [waitlist, setWaitlist] =
    useState<ServerRequestsWaitlistData[]>(loadedWaitlist);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedParker, setSelectedParker] =
    useState<ServerRequestsWaitlistData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [pendingReorderEvent, setPendingReorderEvent] = useState<any>(null);
  const [draggedItemId, setDraggedItemId] = useState<number | null>(null);
  const [isWaitlistEnabled, setIsWaitlistEnabled] = useState(
    loadedWaitlistToggle.isWaitlistEnabled
  );
  const [showWaitlistConfirmModal, setShowWaitlistConfirmModal] =
    useState(false);

  const [search, setSearch] = useState("");

  const [visibleCount, setVisibleCount] = useState(10);

  const waitlistFiltered = useMemo(() => {
    return waitlist.filter((p) => {
      const q = search.toLowerCase();
      return (
        p.parkerName?.toLowerCase().includes(q) ||
        p.parkerEmail?.toLowerCase().includes(q) ||
        p.planName?.toLowerCase().includes(q)
      );
    });
  }, [waitlist, search]);

  const shownWaitlist = useMemo(() => {
    return waitlistFiltered.slice(0, visibleCount);
  }, [waitlistFiltered, visibleCount]);

  const waitlistApi = createApiService(WaitlistApi);

  const removeParker = async () => {
    if (!selectedParker) return;

    try {
      const result = await waitlistApi.deleteParkerFromWaitlist({
        id: selectedParker.id!,
      });

      if (result.data.error) {
        toast.error(result.data.error);
        return;
      }

      toast.success("Parker removed from waitlist.");
      setShowRemoveModal(false);

      setWaitlist((prev) => prev.filter((p) => p.id !== selectedParker.id));
    } catch (err: any) {
      toast.error("An unexpected error occurred. Please try again later.");
    }
  };

  const handleInvite = async (
    parker: ServerRequestsWaitlistData,
    holdPeriodHours: number
  ) => {
    if (!parker?.id) return;

    try {
      const result = await waitlistApi.inviteParkerToClaimSpot({
        id: parker.id,
        serverRequestsInviteParkerToClaimSpotRequest: {
          waitlistEntryId: parker.id,
          holdPeriodHours: holdPeriodHours,
        },
      });

      if (result.data.error) {
        toast.error(result.data.error);
        return;
      }

      toast.success("Parker invited.");

      setWaitlist((prev) =>
        prev.map((p) => (p.id === result.data.id ? result.data : p))
      );
    } catch (err: any) {
      toast.error("An unexpected error occurred. Please try again later.");
    }
  };

  const onRowDragMove = (event: any) => {
    const node = event.node;
    if (node?.data?.id) {
      setDraggedItemId(node.data.id);
    }
  };

  const handleRowDragEnd = (e: any) => {
    const newOrderIds: number[] = [];
    e.api.forEachNodeAfterFilterAndSort((node: any) => {
      if (node?.data?.id) newOrderIds.push(node.data.id);
    });

    const movedId = draggedItemId;
    const targetIndex = newOrderIds.indexOf(movedId!);

    if (targetIndex === -1) {
      toast.error("Failed to determine new position.");
      return;
    }

    setPendingReorderEvent({ movedId, targetIndex, newOrderIds });
    setShowReorderModal(true);
  };

  const handleReorder = async () => {
    if (!pendingReorderEvent) return;

    const { movedId, targetIndex } = pendingReorderEvent;

    try {
      const result = await waitlistApi.reorderWaitlist({
        id: movedId,
        serverRequestsReorderWaitlistRequest: {
          newIndex: targetIndex,
        },
      });

      if (result.data.error) {
        toast.error(result.data.error);
        return;
      }

      setWaitlist(result.data.items!);
      toast.success("Waitlist reordered.");
    } catch {
      toast.error("An unexpected error occurred. Please try again later.");
    } finally {
      setShowReorderModal(false);
      setPendingReorderEvent(null);
    }
  };

  const handleWaitlistToggle = async (enabled) => {
    try {
      const response = await waitlistApi.patchFacilityWaitlistToggle({
        facilityId: facilityId,
        serverRequestsPatchFacilityWaitlistToggleRequest: {
          isWaitlistEnabled: enabled,
        },
      });

      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }

      setIsWaitlistEnabled(response.data.isWaitlistEnabled);

      toast.success(
        `Waitlist ${response.data.isWaitlistEnabled ? "enabled" : "disabled"}.`
      );
    } catch {
      toast.error("An unexpected error occurred. Please try again later.");
    } finally {
      setShowWaitlistConfirmModal(false);
    }
  };

  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: "parkerName",
        headerName: "Name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
        rowDrag: true,
      },
      {
        field: "parkerEmail",
        headerName: "Email",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 180,
      },
      {
        field: "planName",
        headerName: "Plan",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "spacesRequested",
        headerName: "Spaces",
        sortable: true,
        filter: true,
        width: 100,
        maxWidth: 120,
      },
      {
        field: "dateRequested",
        headerName: "Request Date",
        sortable: true,
        filter: true,
        width: 160,
        maxWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString();
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        filter: true,
        width: 140,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          const onHoldStatus = DataEntitiesWaitlistStatus.NUMBER_1;
          const now = new Date();
          const holdUntil = new Date(params.data.holdUntil);

          let label = "WAITLISTED";

          if (params.value === onHoldStatus) {
            const diffMs = holdUntil.getTime() - now.getTime();
            const hoursLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60)));
            label =
              hoursLeft > 0
                ? `ON HOLD - ${hoursLeft} hrs left`
                : "ON HOLD - expired";
          }

          return (
            <span
              className={`badge ${
                params.value === onHoldStatus ? "bg-warning" : "bg-secondary"
              }`}
            >
              {label}
            </span>
          );
        },
      },
      {
        field: "",
        headerName: "",
        sortable: false,
        filter: false,
        width: 160,
        maxWidth: 160,
        cellRenderer: (params: any) => {
          const parker = params.data;
          const onHoldStatus = DataEntitiesWaitlistStatus.NUMBER_1;
          const isOnHold = parker.status === onHoldStatus;

          return (
            <div
              className="d-flex gap-2 justify-content-center align-items-center"
              style={{ height: "100%" }}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="invite-parker-tooltip">
                    Invite Parker to Claim Spot
                  </Tooltip>
                }
              >
                <button
                  className="btn btn-sm"
                  style={{
                    visibility: isOnHold ? "hidden" : "visible",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedParker(parker);
                    setShowModal(true);
                  }}
                >
                  <CheckCircleFill />
                </button>
              </OverlayTrigger>

              <button
                className="btn btn-sm"
                title="Remove from Waitlist"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedParker(parker);
                  setShowRemoveModal(true);
                }}
              >
                <Trash />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <Container fluid>
      <Row className="mb-3 mt-3">
        <Col>
          <div className="d-flex align-items-center gap-2">
            <div className="form-check form-switch d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="switch-waitlist-toggle"
                checked={isWaitlistEnabled}
                onChange={() => setShowWaitlistConfirmModal(true)}
                style={{
                  backgroundColor: isWaitlistEnabled ? "#D61F84" : "",
                  borderColor: isWaitlistEnabled ? "#D61F84" : "",
                }}
              />
              <label
                className="form-check-label ms-2"
                htmlFor="switch-waitlist-toggle"
              >
                {isWaitlistEnabled
                  ? "Waitlist Is Enabled"
                  : "Waitlist Is Disabled"}
              </label>
            </div>
          </div>
        </Col>
      </Row>
      <Row className="d-none d-md-block">
        <Col>
          <div style={{ height: "600px", width: "100%" }}>
            <DataGrid
              rowData={[...waitlist].sort((a, b) => a.order! - b.order!)}
              columnDefs={columns}
              suppressRowClickSelection={true}
              rowDragManaged={true}
              onRowDragMove={onRowDragMove}
              onRowDragEnd={handleRowDragEnd}
            />
          </div>
        </Col>
      </Row>

      <Row className="d-block d-md-none">
        <Col>
          <Form.Control
            type="text"
            placeholder="Search waitlist..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setVisibleCount(10);
            }}
            className="mb-3 mt-3 sticky-top bg-white"
          />

          {shownWaitlist.length === 0 ? (
            <div className="text-center text-muted py-3">No Rows to Show</div>
          ) : (
            shownWaitlist.map((p, idx) => {
              const onHoldStatus = DataEntitiesWaitlistStatus.NUMBER_1;
              const now = new Date();
              const holdUntil = p.holdUntil
                ? new Date(p.holdUntil as any)
                : null;

              let statusLabel = "WAITLISTED";
              if (p.status === onHoldStatus && holdUntil) {
                const diffMs = holdUntil.getTime() - now.getTime();
                const hoursLeft = Math.max(
                  0,
                  Math.ceil(diffMs / (1000 * 60 * 60))
                );
                statusLabel =
                  hoursLeft > 0
                    ? `ON HOLD - ${hoursLeft} hrs left`
                    : "ON HOLD - expired";
              }

              return (
                <Card key={idx} className="mb-3 shadow-sm rounded-4 border-0">
                  <Card.Body>
                    <div className="d-flex flex-column mb-3">
                      <h6 className="mb-2 fw-bold">{p.parkerName}</h6>

                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          className="btn-main flex-fill"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedParker(p);
                            setShowModal(true); 
                          }}
                        >
                          Approve
                        </Button>

                        <Button
                          size="sm"
                          variant="light"
                          className="flex-fill"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedParker(p);
                            setShowRemoveModal(true);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>

                    <div className="row g-2 text-center">
                      <div className="col-6">
                        <div className="bg-light rounded-3 py-3">
                          <div className="text-muted small">PLAN</div>
                          <div className="fw-bold fs-6">{p.planName}</div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="bg-light rounded-3 py-3">
                          <div className="text-muted small">SPACES</div>
                          <div className="fw-bold fs-6">
                            {p.spacesRequested}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="bg-light rounded-3 py-3">
                          <div className="text-muted small">REQUEST DATE</div>
                          <div className="fw-bold fs-6">
                            {p.dateRequested
                              ? new Date(
                                  p.dateRequested as any
                                ).toLocaleDateString("en-US")
                              : "—"}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="bg-light rounded-3 py-3">
                          <div className="text-muted small">STATUS</div>
                          <div className="fw-bold fs-6">
                            {p.status ===
                            DataEntitiesWaitlistStatus.NUMBER_1 ? (
                              <Badge bg="warning">On Hold</Badge>
                            ) : (
                              <Badge bg="secondary">Waitlisted</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              );
            })
          )}

          {visibleCount < waitlistFiltered.length &&
            shownWaitlist.length > 0 && (
              <div className="text-center">
                <Button
                  className="btn-main"
                  onClick={() => setVisibleCount((v) => v + 10)}
                >
                  Load More
                </Button>
              </div>
            )}
        </Col>
      </Row>

      <ConfirmModal
        title="Remove Parker"
        message="Are you sure you want to remove this parker from the waitlist?"
        show={showRemoveModal}
        setShow={setShowRemoveModal}
        onConfirm={removeParker}
      />
      <ConfirmModal
        title="Change Waitlist Order"
        message="Are you sure you want to reorder the waitlist? This will affect parker priority."
        show={showReorderModal}
        setShow={setShowReorderModal}
        onConfirm={handleReorder}
      />
      <ConfirmModal
        title="Change Waitlist Setting"
        message={`Are you sure you want to ${
          isWaitlistEnabled ? "disable" : "enable"
        } the waitlist?`}
        show={showWaitlistConfirmModal}
        setShow={setShowWaitlistConfirmModal}
        onConfirm={() => {
          handleWaitlistToggle(!isWaitlistEnabled);
        }}
      />
      <InviteParkerModal
        show={showModal}
        parkerName={selectedParker?.parkerName ?? ""}
        defaultHoldPeriod={48}
        onConfirm={async (holdPeriodHours) => {
          await handleInvite(selectedParker!, holdPeriodHours);
          setShowModal(false);
        }}
        onCancel={() => setShowModal(false)}
      />
    </Container>
  );
}
